import paper from "paper";
import { ToolBase } from "./ToolBase";
import { setAngleHint } from "../snapAngleIndicator";
import type { PointerSample } from "../canvas/PointerInput";
import type { ConstructionLine, Piece, Project, Vec2 } from "../model/Project";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapExclusion,
  type DrawSnapTargets,
} from "./drawSnap";
import {
  pieceForStitchLine,
  pieceStitchLines,
  projectConstructionLines,
} from "../model/Project";
import {
  translatePiece,
  translateDecoration,
  translatePathData,
  addPieceLayoutDelta,
  rotatePiece,
  rotateCutout,
  rotateStitchLine,
  transformPieceByAffine,
  resizeCutoutByBox,
  resizeStitchLineByBox,
  pieceDisplayOffset,
} from "../model/pieceTransform";
import { translateConstructionLine } from "../model/constructionTranslate";
// Re-exported so existing callers keep importing from here; the canonical
// implementations live in the model (#1165) so model code can use them.
export {
  translateConstructionLine,
  translatePrimitive,
} from "../model/constructionTranslate";
import {
  TransformController,
  type TransformHost,
  type TransformTarget,
} from "./transformController";
import type { Scene, OutlineEditHandlesSpec } from "../canvas/Scene";
import type { ToolSettings } from "./EditorContext";
import type { Box } from "../model/constructionResize";
import { updatePiece } from "../model/projectOps";
import { translatePivotPoint } from "../model/pieceTransform";
import {
  angleModifierHeld,
  isAngleSnapActive,
  isGridSnapActive,
  pickHandlePoint,
  snapAlongRayToGrid,
  snapToCommonAngle,
} from "./dragSnap";
import { translateAndCropStitchLine } from "../model/stitchCrop";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  NoteDragController,
  applyNoteTipOffsets,
  noteTipsForPieces,
  type NoteTipRef,
} from "./noteDrag";

interface DragTarget {
  kind: "piece" | "decoration" | "hole" | "cutout" | "stitch";
  pieceId: string;
  decorationId?: string;
  holeId?: string;
  cutoutId?: string;
  stitchId?: string;
  /** For `kind === "piece"`: the full set of part ids being dragged
   *  together. Always contains `pieceId`; extra ids land here when
   *  the user grabbed a member of an existing multi-piece selection
   *  (issue #251 multi-select). Sub-item drags don't multi-select. */
  pieceIds?: string[];
}

export class SelectTool extends ToolBase implements TransformHost {
  id = "select";
  label = "Select";
  shortcut = "v";
  // Classic OS mouse-cursor pointer: tilted arrow pointing up-left.
  // Uses currentColor so it follows the toolbar's text color (light or dark).
  cursor = "default";

  /** Drag state — set on `onDown` and cleared on `onUp` / `onCancel`. */
  private dragStart: { x: number; y: number } | null = null;
  private dragLast: { x: number; y: number } | null = null;
  private dragTarget: DragTarget | null = null;
  private draggedItem: paper.Item | null = null;
  /** Extra paper items translated alongside `draggedItem` when a
   *  multi-piece selection is being dragged together (issue #251).
   *  Empty for sub-item drags and single-piece drags. */
  private extraDraggedItems: paper.Item[] = [];
  /** Grid-snap state for piece drags. `snapCorner` is the project-coord
   *  position of whichever bbox corner the user grabbed near at drag start —
   *  that corner is what gets pulled to nearby grid lines as the drag
   *  progresses. `snapApplied` is the cumulative snap delta already
   *  written to the visual so per-frame onMove can apply just the diff. */
  private snapCorner: { x: number; y: number } | null = null;
  private snapApplied: { dx: number; dy: number } = { dx: 0, dy: 0 };

  /** Callout tips belonging to notes pinned to the part(s) being dragged, so
   *  they travel with the leather instead of staying behind until commit
   *  (#157). The cards themselves stay put — where the maker parked them. */
  private draggedNoteTips: NoteTipRef[] = [];

  /** On-canvas rotate / resize handles for the selected leather object (#971).
   *  The Select tool OWNS the controller and drives it by composition. */
  private transform = new TransformController(this);

  /** Moving and re-pointing notes, shared with the Notes tool (#157). */
  private readonly noteDrag = new NoteDragController(this.ctx);

  // ---- TransformHost ---------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }
  getScene(): Scene {
    return this.ctx.scene;
  }
  getSettings(): ToolSettings {
    return this.ctx.settings;
  }
  refreshEditCursor(p: { x: number; y: number }): void {
    this.applyTransformCursor(p);
  }

  /** Snap targets for the resize drag in progress, gathered once per drag
   *  (the scene doesn't change mid-gesture) without the object being resized. */
  private resizeSnap: { key: string; targets: DrawSnapTargets } | null = null;

  snapResizeHandle(cursor: Vec2, targetKey: string): Vec2 {
    if (this.resizeSnap?.key !== targetKey) {
      this.resizeSnap = {
        key: targetKey,
        targets: gatherDrawSnapTargets(
          this.ctx.history.state,
          this.ctx.scene.getPaperScope(),
          this.ctx.scene.getPartLayoutView(),
          snapExclusionForKey(targetKey),
        ),
      };
    }
    const result = resolveDrawSnap(this.ctx, cursor, this.resizeSnap.targets);
    this.ctx.scene.setDrawSnapPoint(result.hit?.pos ?? null);
    return result.point;
  }

  clearResizeHandleSnap(): void {
    this.resizeSnap = null;
    this.ctx.scene.setDrawSnapPoint(null);
  }

  override onActivate(): void {
    this.ctx.scene.setTransformOnSelect(true);
  }
  override onDeactivate(): void {
    this.transform.reset();
    this.ctx.scene.setTransformOnSelect(false);
  }
  override onHover(_s: PointerSample, p: { x: number; y: number }): void {
    this.applyTransformCursor(p);
  }

  private applyTransformCursor(p: { x: number; y: number }): void {
    if (this.transform.hasActiveDrag()) return;
    const box = this.getTransformTarget()?.bounds();
    const handle = box ? this.transform.handleAt(p, box) : null;
    // A transform handle wins: it is the smaller, more deliberate target, and
    // a note card parked under one would otherwise make it unusable.
    const overNote = handle ? null : this.noteDrag.cursorOver(p);
    setCanvasCursor(
      this.ctx.scene.getPaperScope(),
      handle ? this.transform.cursorFor(handle) : (overNote ?? "default"),
    );
  }

  /** The transform target for the current selection — a single, non-linked
   *  piece / cutout / seam. Null for multi-select, holes, decorations, or
   *  construction-linked / cropped objects (which re-derive; edit the source). */
  private transformSpec(): OutlineEditHandlesSpec | null {
    const sel = this.ctx.scene.getSelection();
    if (!sel.pieceId) return null;
    if ((sel.pieceIds?.length ?? 0) > 1) return null;
    if ((sel.stitchIds?.length ?? 0) > 1) return null;
    if (sel.holeId || sel.decorationId) return null;
    const piece = this.ctx.history.state.pieces.find(
      (p) => p.id === sel.pieceId,
    );
    if (!piece) return null;
    if (sel.cutoutId) {
      const c = piece.cutouts.find((c) => c.id === sel.cutoutId);
      if (!c || c.constructionOutline) return null;
      return { kind: "cutout", pieceId: piece.id, cutoutId: c.id };
    }
    if (sel.stitchId) {
      const l = pieceStitchLines(piece).find((l) => l.id === sel.stitchId);
      if (!l || l.constructionOutline || l.authoredPoints) return null;
      return { kind: "stitch", pieceId: piece.id, stitchId: l.id };
    }
    if (piece.constructionOutline) return null;
    return { kind: "piece", pieceId: piece.id };
  }

  getTransformTarget(): TransformTarget | null {
    const spec = this.transformSpec();
    if (!spec) return null;
    const scope = this.getScope();
    const bounds = (): Box | null => this.ctx.scene.transformBoxFor(spec);
    const rotation = () => this.ctx.scene.transformRotationFor(spec);
    const findPiece = (): Piece | null =>
      this.ctx.history.state.pieces.find((p) => p.id === spec.pieceId) ?? null;

    // One pure per-kind transform fn drives BOTH the live preview and the
    // commit, so the rehearsed drag and the committed result can never drift
    // (#971). Each maps a whole PART to the transformed PART — a directly
    // selected cutout / seam transforms just that child within its part, so the
    // Scene always re-renders the same composite piece live.
    const rotatePart = (
      pc: Piece,
      deg: number,
      cx: number,
      cy: number,
    ): Piece => {
      if (spec.kind === "piece") return rotatePiece(pc, deg, cx, cy, scope);
      if (spec.kind === "cutout")
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) =>
            c.id === spec.cutoutId ? rotateCutout(c, deg, cx, cy, scope) : c,
          ),
        };
      return {
        ...pc,
        stitchLines: (pc.stitchLines ?? []).map((l) =>
          l.id === spec.stitchId ? rotateStitchLine(l, deg, cx, cy, scope) : l,
        ),
      };
    };
    const resizePart = (
      pc: Piece,
      orig: Box,
      next: Box,
      flip: { x: boolean; y: boolean },
    ): Piece => {
      if (spec.kind === "piece")
        return transformPieceByAffine(pc, orig, next, flip, scope);
      if (spec.kind === "cutout")
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) =>
            c.id === spec.cutoutId
              ? resizeCutoutByBox(c, orig, next, flip, scope)
              : c,
          ),
        };
      return {
        ...pc,
        stitchLines: (pc.stitchLines ?? []).map((l) =>
          l.id === spec.stitchId
            ? resizeStitchLineByBox(l, orig, next, flip, scope)
            : l,
        ),
      };
    };

    /** The selected object itself — a part, or the cutout / seam inside it. */
    const findObject = (): { pivotPoint?: Vec2 } | null => {
      const pc = findPiece();
      if (!pc) return null;
      if (spec.kind === "piece") return pc;
      if (spec.kind === "cutout")
        return pc.cutouts.find((c) => c.id === spec.cutoutId) ?? null;
      return pieceStitchLines(pc).find((l) => l.id === spec.stitchId) ?? null;
    };
    /** Set (or clear, with null) the parked pivot on the selected object. */
    const setPivot = (pc: Piece, pv: Vec2 | null): Piece => {
      const apply = <T extends { pivotPoint?: Vec2 }>(o: T): T => {
        const nextObj = { ...o };
        if (pv) nextObj.pivotPoint = pv;
        else delete nextObj.pivotPoint;
        return nextObj;
      };
      if (spec.kind === "piece") return apply(pc);
      if (spec.kind === "cutout")
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) =>
            c.id === spec.cutoutId ? apply(c) : c,
          ),
        };
      return {
        ...pc,
        stitchLines: (pc.stitchLines ?? []).map((l) =>
          l.id === spec.stitchId ? apply(l) : l,
        ),
      };
    };

    const common = {
      pieceId: spec.pieceId,
      bounds,
      rotation,
      minSize: 1,
      // A cutout / seam on a linked part moved in the Spread view is drawn at
      // the part's cut-layout offset (#1140); the controller maps the cursor
      // back into model space by it.
      displayOffset: (): Vec2 => {
        const pc = findPiece();
        if (!pc) return { x: 0, y: 0 };
        const { dx, dy } = pieceDisplayOffset(
          pc,
          this.ctx.scene.getPartLayoutView(),
        );
        return { x: dx, y: dy };
      },
      pivot: (): Vec2 | null => findObject()?.pivotPoint ?? null,
      commitPivot: (label: string, pv: Vec2 | null) =>
        this.ctx.commit(label, (proj) =>
          updatePiece(proj, spec.pieceId, (pc) => setPivot(pc, pv)),
        ),
      previewRotate: (deg: number, cx: number, cy: number): Piece | null => {
        const pc = findPiece();
        return pc ? rotatePart(pc, deg, cx, cy) : null;
      },
      previewResize: (
        orig: Box,
        next: Box,
        flip: { x: boolean; y: boolean },
      ): Piece | null => {
        const pc = findPiece();
        return pc ? resizePart(pc, orig, next, flip) : null;
      },
      commitRotate: (label: string, deg: number, cx: number, cy: number) =>
        this.ctx.commit(label, (proj) =>
          updatePiece(proj, spec.pieceId, (pc) => rotatePart(pc, deg, cx, cy)),
        ),
      commitResize: (
        label: string,
        orig: Box,
        next: Box,
        flip: { x: boolean; y: boolean },
      ) =>
        this.ctx.commit(label, (proj) =>
          updatePiece(proj, spec.pieceId, (pc) =>
            resizePart(pc, orig, next, flip),
          ),
        ),
    };

    if (spec.kind === "piece")
      return {
        ...common,
        key: `piece:${spec.pieceId}`,
        noun: "part",
        allowsEdgeResize: true,
        aspectLockOnly: false,
      };
    if (spec.kind === "cutout")
      return {
        ...common,
        key: `cutout:${spec.pieceId}:${spec.cutoutId}`,
        noun: "cutout",
        allowsEdgeResize: true,
        aspectLockOnly: false,
      };
    return {
      ...common,
      key: `stitch:${spec.pieceId}:${spec.stitchId}`,
      noun: "seam",
      // Same answers as a part and a cutout since #1055. A seam is not a
      // different kind of resize — it was opted out of both behaviours to hide
      // what a straight seam's zero-height box did to the aspect math, which is
      // fixed at the source now (`aspectScale`, `offeredEdges`).
      allowsEdgeResize: true,
      aspectLockOnly: false,
    };
  }

  /** A rotate / resize / pivot drag, or moving an object, is deliberate
   *  placement of an existing object, so a second finger landing must not
   *  abandon it (#40) — on a tablet that contact is nearly always the hand you
   *  are resting — and a browser-cancelled pointer commits it where it was
   *  (#1112). Sketching gestures keep the default, where the second finger
   *  wins. */
  override gestureSurvivesSecondContact(): boolean {
    return this.transform.hasActiveDrag() || this.draggedItem !== null;
  }

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    // Grabbing a rotate/resize handle (#971) beats hit-testing the object.
    // A finger grabs wider than a cursor does (#40) — the knob is a 6 px
    // target, which a fingertip cannot reliably find and, missing it, silently
    // does nothing at all.
    if (this.transform.maybeStartDrag(p, s.pointerType)) return;
    // A note under the cursor is grabbable from here too (#157) — a card sits
    // ON the drawing, so a maker who wants it out of the way should not have
    // to leave whatever mode they are working in to nudge it. Checked before
    // the leather hit test for the same reason it draws on top: the card is
    // the nearer object.
    if (this.noteDrag.begin(p)) return;
    const scope = this.ctx.scene.getPaperScope();
    const hit = this.ctx.scene.hitTest(new scope.Point(p.x, p.y));
    let target: DragTarget | null = null;
    if (hit.decorationId && hit.pieceId) {
      this.ctx.setSelection({
        pieceId: hit.pieceId,
        decorationId: hit.decorationId,
      });
      target = {
        kind: "decoration",
        pieceId: hit.pieceId,
        decorationId: hit.decorationId,
      };
    } else if (hit.holeId && hit.pieceId) {
      this.ctx.setSelection({
        pieceId: hit.pieceId,
        decorationId: null,
        holeId: hit.holeId,
      });
      target = { kind: "hole", pieceId: hit.pieceId, holeId: hit.holeId };
    } else if (hit.cutoutId && hit.pieceId) {
      this.ctx.setSelection({
        pieceId: hit.pieceId,
        decorationId: null,
        cutoutId: hit.cutoutId,
      });
      target = { kind: "cutout", pieceId: hit.pieceId, cutoutId: hit.cutoutId };
    } else if (hit.stitchId && hit.pieceId) {
      // Multi-stitch selection for "Match selected" (#199 path B): Shift =
      // additive, Cmd/Ctrl = toggle, plain click single-selects. The set is
      // keyed by stitch id alone so it can span two pieces (a match may link
      // seams on different parts). A modifier-click only extends the selection —
      // it never starts a drag.
      const additive = s.shiftKey;
      const toggle = s.ctrlKey || s.metaKey;
      if (additive || toggle) {
        const currentIds = this.ctx.scene.getSelection().stitchIds ?? [];
        const inSet = currentIds.includes(hit.stitchId);
        const nextIds = toggle
          ? inSet
            ? currentIds.filter((id) => id !== hit.stitchId)
            : [...currentIds, hit.stitchId]
          : inSet
            ? currentIds
            : [...currentIds, hit.stitchId];
        const leadId = nextIds[nextIds.length - 1] ?? null;
        const leadPieceId = leadId
          ? (pieceForStitchLine(this.ctx.history.state, leadId)?.id ??
            hit.pieceId)
          : null;
        this.ctx.setSelection({
          pieceId: leadPieceId,
          decorationId: null,
          stitchId: leadId,
          stitchIds: nextIds,
        });
        return;
      }
      this.ctx.setSelection({
        pieceId: hit.pieceId,
        decorationId: null,
        stitchId: hit.stitchId,
      });
      target = { kind: "stitch", pieceId: hit.pieceId, stitchId: hit.stitchId };
    } else if (hit.pieceId) {
      // Multi-piece selection (issue #251):
      //   - Shift  → additive: union the hit into the current set.
      //   - Cmd/Ctrl → toggle: flip the hit in/out of the set; clicking
      //     a selected one while holding the modifier removes it.
      //   - no modifier → replace: single-select the hit. If the hit
      //     is already part of a multi-piece set, keep the whole set so
      //     a follow-up drag moves every member; the gesture only
      //     collapses to single-select on the matching onUp.
      const additive = s.shiftKey;
      const toggle = s.ctrlKey || s.metaKey;
      const currentIds = this.ctx.scene.getSelection().pieceIds ?? [];
      const inSet = currentIds.includes(hit.pieceId);
      let nextIds: string[];
      if (toggle) {
        nextIds = inSet
          ? currentIds.filter((id) => id !== hit.pieceId)
          : [...currentIds, hit.pieceId];
      } else if (additive) {
        nextIds = inSet ? currentIds : [...currentIds, hit.pieceId];
      } else if (inSet && currentIds.length > 1) {
        // Plain click on an already-multi-selected piece: keep the
        // set intact so a drag moves everyone. The matching onUp
        // collapses to a single-select if the gesture was a click.
        nextIds = currentIds;
      } else {
        nextIds = [hit.pieceId];
      }
      this.ctx.setSelection({
        pieceId: nextIds[nextIds.length - 1] ?? null,
        pieceIds: nextIds,
        decorationId: null,
      });
      if (toggle && !inSet) {
        // A toggle that ADDED the piece doesn't start a drag — the
        // user wants to extend the multi-selection, not move it.
        // Without this gate, a Cmd+click followed by a tiny drift
        // would commit a spurious no-op move.
        return;
      }
      if ((additive || toggle) && nextIds.length === 0) {
        return;
      }
      target = { kind: "piece", pieceId: hit.pieceId, pieceIds: nextIds };
    } else {
      // Empty-canvas click — clear selection. Preserve modifier-held
      // misses without wiping the existing multi-select so the user
      // can shift-click another piece without losing their progress.
      if (!s.shiftKey && !s.ctrlKey && !s.metaKey) {
        this.ctx.setSelection({ pieceId: null, decorationId: null });
      }
      return;
    }
    // Assembled view locks linked parts in place (they show at their
    // construction positions, not their cut-layout). Grabbing one to move it is
    // a no-op — keep the selection but don't start a drag transient, so it never
    // nudges and snaps back (#652).
    const assembled = this.ctx.scene.getPartLayoutView() === "assembled";
    if (
      target.kind === "piece" &&
      assembled &&
      this.isLinkedPiece(target.pieceId)
    ) {
      return;
    }
    this.dragStart = { x: p.x, y: p.y };
    this.dragLast = { x: p.x, y: p.y };
    this.dragTarget = target;
    this.draggedItem = this.findDraggedItem(target);
    this.extraDraggedItems =
      target.kind === "piece" && target.pieceIds && target.pieceIds.length > 1
        ? target.pieceIds
            .filter((id) => id !== target.pieceId)
            // In Assembled view, linked extras stay locked — leave them out of
            // the transient so only the movable (unlinked) parts follow.
            .filter((id) => !(assembled && this.isLinkedPiece(id)))
            .map((id) => this.ctx.scene.getPieceGroup(id))
            .filter((it): it is paper.Item => it !== null)
        : [];
    // Notes pinned to any part in this drag: their tips ride along (#157).
    this.draggedNoteTips =
      target.kind === "piece"
        ? noteTipsForPieces(
            this.ctx.scene.getPaperScope(),
            this.ctx.history.state,
            target.pieceIds ?? [target.pieceId],
          )
        : [];
    this.snapApplied = { dx: 0, dy: 0 };
    this.snapCorner = null;
    // For piece drags, pick the CLEAR reference point closest to the grab —
    // a bbox corner, an edge midpoint, or the centre (#725). That's the point
    // grid-snap pulls to grid lines, the angle-lock ray anchors on, and the
    // guide is drawn from, so you align the corner/point you're holding (the
    // way you'd butt a template corner to a mat line) and measure from it.
    if (target.kind === "piece") {
      const cands = this.pieceHandleCandidates(target.pieceId);
      if (cands) this.snapCorner = pickHandlePoint(p, cands);
    }
  }

  /** Grab-reference candidates for a part drag, in DISPLAYED space (a spread
   *  cut-layout shifts a linked part by its layout delta): the corners, edge
   *  midpoints and centre of the part's OWN frame — its un-rotated box mapped
   *  through its tilt, i.e. exactly where the transform handles sit — plus the
   *  outline's own vertices, the leather analogue of the line anchors
   *  construction offers the same `pickHandlePoint` (#725 parity).
   *
   *  A tilted part's world AABB is deliberately NOT offered: its corners and
   *  edge midpoints float OFF the shape, so anchoring there parked the move
   *  guide in empty space and — since the anchor wasn't over leather — drew the
   *  ray in the pale off-leather accent instead of the dark one (#971 follow-up).
   *  Null when the outline can't be measured. */
  private pieceHandleCandidates(pieceId: string): {
    anchors: { x: number; y: number }[];
    center: { x: number; y: number };
    midpoints: { x: number; y: number }[];
  } | null {
    const piece = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
    if (!piece) return null;
    const { dx, dy } = pieceDisplayOffset(
      piece,
      this.ctx.scene.getPartLayoutView(),
    );
    const scope = this.ctx.scene.getPaperScope();
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({
        pathData: piece.shapePathData,
        insert: false,
      });
    } catch {
      return null;
    }
    // Outline vertices first (they're already in the tilted frame).
    const subs =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    const anchors: { x: number; y: number }[] = [];
    for (const sub of subs) {
      for (const seg of sub.segments ?? []) {
        anchors.push({ x: seg.point.x + dx, y: seg.point.y + dy });
      }
    }
    // The part's own box: measured with the tilt undone, then mapped back, so
    // its corners / edge midpoints land ON the tilted frame.
    const rot =
      piece.rotation !== undefined && piece.rotationCenter
        ? { deg: piece.rotation, center: piece.rotationCenter }
        : null;
    const pivot = rot ? new scope.Point(rot.center.x, rot.center.y) : null;
    if (rot && pivot) cp.rotate(-rot.deg, pivot);
    const b = cp.bounds;
    if (!(b.width > 0 || b.height > 0)) {
      cp.remove();
      return null;
    }
    const x0 = b.x;
    const x1 = b.x + b.width;
    const y0 = b.y;
    const y1 = b.y + b.height;
    const xm = b.x + b.width / 2;
    const ym = b.y + b.height / 2;
    cp.remove();
    const toFrame = (pt: { x: number; y: number }) => {
      const w =
        rot && pivot
          ? new scope.Point(pt.x, pt.y).rotate(rot.deg, pivot)
          : new scope.Point(pt.x, pt.y);
      return { x: w.x + dx, y: w.y + dy };
    };
    return {
      anchors: [
        ...anchors,
        toFrame({ x: x0, y: y0 }),
        toFrame({ x: x1, y: y0 }),
        toFrame({ x: x0, y: y1 }),
        toFrame({ x: x1, y: y1 }),
      ],
      center: toFrame({ x: xm, y: ym }),
      midpoints: [
        toFrame({ x: xm, y: y0 }),
        toFrame({ x: xm, y: y1 }),
        toFrame({ x: x0, y: ym }),
        toFrame({ x: x1, y: ym }),
      ],
    };
  }

  /** Whether a piece is linked to construction lines (its outline re-derives).
   *  Linked parts carry a cut-layout transform instead of absolute geometry. */
  private isLinkedPiece(pieceId: string): boolean {
    return !!this.ctx.history.state.pieces.find((p) => p.id === pieceId)
      ?.constructionOutline;
  }

  private findDraggedItem(target: DragTarget): paper.Item | null {
    if (target.kind === "decoration" && target.decorationId) {
      return this.ctx.scene.getDecorationItem(
        target.pieceId,
        target.decorationId,
      );
    }
    if (target.kind === "hole" && target.holeId) {
      return this.findChildByData(
        target.pieceId,
        "hole",
        "holeId",
        target.holeId,
      );
    }
    if (target.kind === "cutout" && target.cutoutId) {
      return this.findChildByData(
        target.pieceId,
        "cutout",
        "cutoutId",
        target.cutoutId,
      );
    }
    if (target.kind === "stitch" && target.stitchId) {
      return this.findChildByData(
        target.pieceId,
        "stitch",
        "stitchId",
        target.stitchId,
      );
    }
    return this.ctx.scene.getPieceGroup(target.pieceId);
  }

  private findChildByData(
    pieceId: string,
    kind: string,
    idField: string,
    idValue: string,
  ): paper.Item | null {
    const piece = this.ctx.scene.getPieceGroup(pieceId);
    if (!piece || !piece.children) return null;
    const matches = (item: paper.Item) =>
      item.data?.kind === kind && item.data[idField] === idValue;
    for (const child of piece.children) {
      if (matches(child)) return child;
      // Descend one level into container groups — stitch seams render nested
      // inside the clipped "stitching-clip" wrapper, so a direct-children-only
      // scan misses them and the live drag has nothing to translate (#766).
      if (child.children) {
        for (const gc of child.children) if (matches(gc)) return gc;
      }
    }
    return null;
  }

  /** The dragged object's identity in `TransformTarget.key` form, or null when
   *  it can't own a transform overlay: a hole / decoration (no handles), or a
   *  multi-part drag (a multi-selection shows no overlay). Used to decide
   *  whether the visible overlay belongs to what's being dragged. */
  private dragTargetKey(target: DragTarget | null): string | null {
    if (!target) return null;
    if ((target.pieceIds?.length ?? 0) > 1) return null;
    if (target.kind === "piece") return `piece:${target.pieceId}`;
    if (target.kind === "cutout")
      return `cutout:${target.pieceId}:${target.cutoutId}`;
    if (target.kind === "stitch")
      return `stitch:${target.pieceId}:${target.stitchId}`;
    return null;
  }

  /** True when the transform overlay on screen belongs to the object currently
   *  being dragged, so it should move with it. */
  private draggingTransformTarget(target: DragTarget | null): boolean {
    const key = this.dragTargetKey(target);
    return key !== null && key === this.getTransformTarget()?.key;
  }

  /** Drag offset from the grab point. When angle-snap is active — Shift held
   *  OR the angle-snap toggle on (#725) — the move is constrained to the
   *  nearest common drafting angle (0/30/45/60/90/…) at the cursor's distance,
   *  matching the drawing tools' angle snap. If grid-snap is ALSO on, the
   *  endpoint snaps along that ray to the nearest grid-line crossing, so the
   *  user moves in a locked direction and lands on a grid position. Otherwise
   *  plain grid-snap applies as before. Read each frame from the live modifier
   *  so toggling Shift mid-drag engages / releases the lock. */
  private constrainedDelta(
    p: { x: number; y: number },
    shift: boolean,
  ): { dx: number; dy: number } {
    const start = this.dragStart!;
    if (isAngleSnapActive(this.ctx.settings, shift)) {
      // Angle-snap the raw cursor translation, then anchor on the grabbed
      // reference point (corner/edge/centre nearest the grab): that point
      // moves by the translation, and — with grid-snap on — slides along the
      // ray onto the nearest grid crossing so it lands on a grid line (#725).
      const snapped = snapToCommonAngle(start, p);
      const tx = snapped.x - start.x;
      const ty = snapped.y - start.y;
      const ref = this.snapCorner ?? start;
      let end = { x: ref.x + tx, y: ref.y + ty };
      if (isGridSnapActive(this.ctx.settings)) {
        const grid = this.ctx.scene.getMatGridInfo();
        const g = grid ? snapAlongRayToGrid(ref, end, grid) : null;
        if (g) end = g;
      }
      return { dx: end.x - ref.x, dy: end.y - ref.y };
    }
    return this.snapOffset(p.x - start.x, p.y - start.y);
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    if (this.transform.handleMove(s, p)) return;
    if (this.noteDrag.move(p)) return;
    if (!this.dragStart || !this.draggedItem) return;
    const snap = this.constrainedDelta(p, angleModifierHeld(s));
    this.updateDragGuide(p, angleModifierHeld(s), snap);
    const deltaX = snap.dx - this.snapApplied.dx;
    const deltaY = snap.dy - this.snapApplied.dy;
    if (deltaX === 0 && deltaY === 0) return;
    const scope = this.ctx.scene.getPaperScope();
    const delta = new scope.Point(deltaX, deltaY);
    this.draggedItem.translate(delta);
    // Multi-piece drag (issue #251): translate every additional
    // selected part by the same delta so the whole set follows the
    // grabbed piece in lock-step. Paper transforms are cumulative,
    // so the same `delta` applied per frame keeps the cluster aligned.
    for (const item of this.extraDraggedItems) {
      item.translate(delta);
    }
    // The rotate/resize overlay (dashed frame + rotation crosshair) belongs to
    // the dragged object — translate it in lock-step so it tracks the move live
    // instead of snapping into place on commit (#971).
    if (this.draggingTransformTarget(this.dragTarget))
      this.ctx.scene.translateTransformOverlay(deltaX, deltaY);
    // A note's tip is pinned to a point ON the leather, so it has to travel
    // with it — a dot left standing where the part used to be reads as the
    // note having come unstuck (#157). Only the tip end of the leader moves;
    // the card, and so the far end of the line, stays where it was parked.
    // Absolute from the drag's total offset, not a per-frame translate: see
    // `applyNoteTipOffsets`.
    applyNoteTipOffsets(scope, this.draggedNoteTips, snap.dx, snap.dy);
    this.snapApplied = snap;
    this.dragLast = { x: p.x, y: p.y };
    // Same reasoning as the overlay above: the dots belong to the geometry, so
    // they have to travel with it rather than snap into place on commit.
    this.refreshLiveSnapCandidates(snap.dx, snap.dy);
  }

  /** Adjusts a raw drag offset so the dragged piece's grabbed corner snaps
   *  to the nearest grid line on the cutting mat, when both are within a
   *  small threshold. No-op for non-piece drags or when the mat is
   *  hidden. */
  private snapOffset(rawDx: number, rawDy: number): { dx: number; dy: number } {
    if (this.dragTarget?.kind !== "piece") return { dx: rawDx, dy: rawDy };
    if (!this.snapCorner) return { dx: rawDx, dy: rawDy };
    // Gate the piece-corner-to-grid snap on the user-facing Grid
    // toggle (issue #98 follow-up). Previously this fired
    // unconditionally; now it shares one switch with the other
    // grid-snap drags so the toggle behaves consistently.
    if (!isGridSnapActive(this.ctx.settings)) return { dx: rawDx, dy: rawDy };
    const grid = this.ctx.scene.getMatGridInfo();
    if (!grid) return { dx: rawDx, dy: rawDy };
    const threshold = grid.step * 0.25;
    const cornerX = this.snapCorner.x + rawDx;
    const cornerY = this.snapCorner.y + rawDy;
    // Closest vertical grid line — multiple of `step` from innerLeft.
    const nearestX =
      grid.innerLeft +
      Math.round((cornerX - grid.innerLeft) / grid.step) * grid.step;
    // Horizontal lines step upward from innerBottom in y-down coords.
    const nearestY =
      grid.innerBottom -
      Math.round((grid.innerBottom - cornerY) / grid.step) * grid.step;
    let dx = rawDx;
    let dy = rawDy;
    if (Math.abs(cornerX - nearestX) < threshold) {
      dx += nearestX - cornerX;
    }
    if (Math.abs(cornerY - nearestY) < threshold) {
      dy += nearestY - cornerY;
    }
    return { dx, dy };
  }

  override onUp(s: PointerSample, p: { x: number; y: number }) {
    if (this.transform.handleUp(p)) return;
    if (this.noteDrag.end(p)) return;
    const start = this.dragStart;
    const target = this.dragTarget;
    // Commit the snapped, axis-constrained offset (matches what's visible on
    // screen) rather than the raw pointer delta — keeps the model in sync with
    // the snapped visual and the Shift axis-lock (#725).
    const snap = start
      ? this.constrainedDelta(p, angleModifierHeld(s))
      : { dx: 0, dy: 0 };
    this.resetDrag();
    if (!start || !target) return;
    const dx = snap.dx;
    const dy = snap.dy;
    // A tiny click counts as selection-only — the visual transient is zero
    // anyway, so nothing to revert.
    if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
      // Plain click on an already-multi-selected piece (no modifier,
      // no drag) collapses the selection to just that piece. onDown
      // intentionally preserved the multi-set so a follow-up drag
      // could move the cluster; the matching no-drag onUp finishes
      // the collapse.
      if (
        target.kind === "piece" &&
        target.pieceIds &&
        target.pieceIds.length > 1 &&
        !s.shiftKey &&
        !s.ctrlKey &&
        !s.metaKey
      ) {
        this.ctx.setSelection({
          pieceId: target.pieceId,
          pieceIds: [target.pieceId],
          decorationId: null,
        });
      }
      return;
    }
    // A user-parked rotation pivot travels with its object so it keeps its
    // place relative to the geometry (#420 parity). It rides on the MODEL now
    // (#990 follow-up), so every translate below carries it — nothing to do
    // here, and a cancelled drag can't disturb it because nothing was written.
    const moved = this.dragReducer(target, dx, dy);
    this.ctx.commit(moved.label, moved.apply);
  }

  /**
   * The move, as one pure reducer per target kind.
   *
   * Both the COMMIT and the live snap-candidate preview run this, so the dots
   * shown mid-drag are computed by the same code that decides where the object
   * actually lands — the rule the rotate preview already follows (#971). Two
   * implementations would drift, and dots that disagree with the drop are worse
   * than no dots.
   */
  private dragReducer(
    target: DragTarget,
    dx: number,
    dy: number,
  ): { label: string; apply: (proj: Project) => Project } {
    const scope = this.ctx.scene.getPaperScope();
    if (target.kind === "decoration") {
      const decId = target.decorationId!;
      return {
        label: "Move decoration",
        apply: (proj) =>
          translateDecorationInProject(
            proj,
            target.pieceId,
            decId,
            dx,
            dy,
            scope,
          ),
      };
    }
    if (target.kind === "hole") {
      const holeId = target.holeId!;
      return {
        label: "Move hole",
        apply: (proj) =>
          translateHoleInProject(proj, target.pieceId, holeId, dx, dy),
      };
    }
    if (target.kind === "cutout") {
      const cutoutId = target.cutoutId!;
      return {
        label: "Move cutout",
        apply: (proj) =>
          translateCutoutInProject(
            proj,
            target.pieceId,
            cutoutId,
            dx,
            dy,
            scope,
          ),
      };
    }
    if (target.kind === "stitch") {
      const stitchId = target.stitchId!;
      return {
        label: "Move stitch line",
        apply: (proj) =>
          translateStitchLineInProject(
            proj,
            target.pieceId,
            stitchId,
            dx,
            dy,
            scope,
          ),
      };
    }
    const pieceId = target.pieceId;
    const pieceIds =
      target.pieceIds && target.pieceIds.length > 0
        ? target.pieceIds
        : [pieceId];
    // Parts move exactly where the user drops them — no auto overlap
    // resolution on move (it was slow at scale and surprised the user by
    // nudging deliberately-placed / tightly-nested parts apart). Overlap is
    // only auto-resolved on Create, where a new part shouldn't spawn on top
    // of an existing one.
    //
    // A *linked* part (one with a constructionOutline) can't have its geometry
    // rewritten — the construction re-derive would clobber it on commit — so
    // in Spread view its drag accumulates into the cut-layout transform
    // (`layout`) instead, applied at render/export time (#652). In Assembled
    // view linked parts are position-locked (the drag transient never starts
    // for them, see onDown), so this only ever sees them in Spread.
    const spread = this.ctx.scene.getPartLayoutView() === "spread";
    return {
      label: "Move part",
      apply: (proj) => {
        let next = proj;
        for (const id of pieceIds) {
          const piece = next.pieces.find((p) => p.id === id);
          if (piece?.constructionOutline) {
            if (!spread) continue; // linked + assembled → locked, no-op
            next = {
              ...next,
              pieces: next.pieces.map((p) =>
                p.id === id ? addPieceLayoutDelta(p, dx, dy) : p,
              ),
            };
          } else {
            next = translatePieceInProject(next, id, dx, dy, scope);
          }
        }
        return next;
      },
    };
  }

  /** Keep the snap dots on the geometry while it's being dragged, instead of
   *  parked where it started. Costs nothing when the dots are switched off,
   *  which is the default. */
  private refreshLiveSnapCandidates(dx: number, dy: number): void {
    if (!this.ctx.scene.isSnapCandidatesVisible()) return;
    const target = this.dragTarget;
    if (!target) return;
    this.ctx.scene.setSnapCandidateLiveProject(
      this.dragReducer(target, dx, dy).apply(this.ctx.history.state),
    );
  }

  override onCancel() {
    if (this.transform.handleCancel()) return;
    if (this.noteDrag.cancel()) return;
    if (this.draggedItem) {
      // Revert any transient transform by re-rendering from project state.
      this.ctx.rerender();
    }
    this.resetDrag();
  }

  /** Show or hide the angle-snap guide (origin dot + directional ray +
   *  endpoint arrow + distance label) and HUD angle chip for the current
   *  drag frame (#725). Visible only while angle-snap is active (Shift or
   *  toggle) and the drag has moved off the grab point. `snap` is the
   *  already-resolved (angle/grid-constrained) delta. */
  private updateDragGuide(
    p: { x: number; y: number },
    shift: boolean,
    snap: { dx: number; dy: number },
  ) {
    const start = this.dragStart;
    const ref = this.snapCorner ?? start;
    if (start && ref && Math.hypot(p.x - start.x, p.y - start.y) > 0.2) {
      // Show the move vector + distance for ANY drag, anchored at the grabbed
      // reference point (`snap` is already that point's translation). The HUD
      // angle chip is shown only while angle-snapping.
      const to = { x: ref.x + snap.dx, y: ref.y + snap.dy };
      // Everything this tool drags is a leather object, so the guide always
      // takes the dark over-leather accent — the anchor can legitimately sit
      // just off the shape (a curved part's frame corner), and letting the ray
      // flip pale there only read as a glitch.
      this.ctx.scene.setDragAxisGuide(ref, to, { overLeather: true });
      setAngleHint(
        isAngleSnapActive(this.ctx.settings, shift)
          ? Math.atan2(snap.dy, snap.dx)
          : null,
      );
    } else {
      this.ctx.scene.clearDragAxisGuide();
      setAngleHint(null);
    }
  }

  private resetDrag() {
    this.dragStart = null;
    this.dragLast = null;
    this.dragTarget = null;
    this.draggedItem = null;
    this.extraDraggedItems = [];
    this.draggedNoteTips = [];
    this.snapCorner = null;
    this.snapApplied = { dx: 0, dy: 0 };
    this.ctx.scene.clearDragAxisGuide();
    // Hand the dots back to the committed model — on release it now holds the
    // moved geometry, and on cancel it holds the original.
    this.ctx.scene.setSnapCandidateLiveProject(null);
    setAngleHint(null);
  }
}

export function translatePieceInProject(
  proj: Project,
  pieceId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) =>
      p.id === pieceId ? translatePiece(p, dx, dy, scope) : p,
    ),
  };
}

export function translateDecorationInProject(
  proj: Project,
  pieceId: string,
  decorationId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) =>
      p.id !== pieceId
        ? p
        : {
            ...p,
            decorations: p.decorations.map((d) =>
              d.id === decorationId ? translateDecoration(d, dx, dy, scope) : d,
            ),
          },
    ),
  };
}

export function translateHoleInProject(
  proj: Project,
  pieceId: string,
  holeId: string,
  dx: number,
  dy: number,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) =>
      p.id !== pieceId
        ? p
        : {
            ...p,
            holes: p.holes.map((h) =>
              h.id === holeId
                ? {
                    ...h,
                    position: { x: h.position.x + dx, y: h.position.y + dy },
                  }
                : h,
            ),
          },
    ),
  };
}

export function translateCutoutInProject(
  proj: Project,
  pieceId: string,
  cutoutId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) =>
      p.id !== pieceId
        ? p
        : {
            ...p,
            cutouts: p.cutouts.map((c) =>
              c.id === cutoutId
                ? translatePivotPoint(
                    {
                      ...c,
                      pathData: translatePathData(c.pathData, dx, dy, scope),
                    },
                    dx,
                    dy,
                  )
                : c,
            ),
          },
    ),
  };
}

export function translateConstructionLineInProject(
  proj: Project,
  constructionId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    constructionLines: projectConstructionLines(proj).map((l) =>
      l.id === constructionId ? translateConstructionLine(l, dx, dy, scope) : l,
    ),
  };
}

/** Translate every construction line in `closure` (except those in
 *  `originIds`, which the caller already translated) by `(dx, dy)`
 *  in the model. Commit-time counterpart to the live-drag
 *  `translateDragClosure` in ConstructionTool — keeps the snap-chain
 *  partners + offset descendants + transitive snap-chain partners
 *  of offsets all in lock-step at commit. Replaces the legacy
 *  snap-only `propagateCoincidentAfterLineMove` which couldn't see
 *  across offset edges and left lines snapped to offsets stranded
 *  at their pre-drag positions. */
export function translateClosureInProject(
  proj: Project,
  closure: ReadonlySet<string>,
  originIds: ReadonlySet<string>,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  if (closure.size === 0) return proj;
  if (dx === 0 && dy === 0) return proj;
  return {
    ...proj,
    constructionLines: projectConstructionLines(proj).map((l) => {
      if (!closure.has(l.id)) return l;
      if (originIds.has(l.id)) return l;
      return translateConstructionLine(l, dx, dy, scope);
    }),
  };
}

/** Apply a rigid rotation to a construction-line primitive about
 *  `(cx, cy)`, returning the rotated copy. The behaviour depends on
 *  the primitive's parameterisation:
 *  - **rectangle / ellipse** carry an axis-aligned bbox (`x`, `y`,
 *    `width`, `height`); they have no field that could express tilt,
 *    so the primitive is returned **unchanged** and the caller is
 *    expected to track the rotation separately via
 *    `ConstructionLine.rotation` / `rotationCenter`.
 *  - **line / pill** carry explicit endpoints (`x1, y1, x2, y2`);
 *    orientation is encoded directly in those endpoints, so the
 *    endpoints are rotated to match the new orientation. There's no
 *    redundant `rotation` field tracking — `rotateConstructionLine`
 *    clears it for endpoint-based primitives.
 *
 *  This split is what lets a rotated rectangle survive a `Duplicate`
 *  with its `primitive.kind` intact (issue #168): pre-fix, rotation
 *  unconditionally deleted the primitive metadata; now it rotates or
 *  preserves it according to whichever encoding the kind uses. */
export function rotatePrimitive(
  prim: NonNullable<Project["constructionLines"]>[number]["primitive"],
  deg: number,
  cx: number,
  cy: number,
): NonNullable<Project["constructionLines"]>[number]["primitive"] {
  if (!prim) return prim;
  if (Math.abs(deg) < 1e-6) return prim;
  if (prim.kind === "rectangle" || prim.kind === "ellipse") {
    // Tilt is carried by the line's `rotation` field; the primitive's
    // un-rotated bbox stays unchanged.
    return prim;
  }
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const rot = (px: number, py: number) => ({
    x: cx + (px - cx) * cos - (py - cy) * sin,
    y: cy + (px - cx) * sin + (py - cy) * cos,
  });
  const p1 = rot(prim.x1, prim.y1);
  const p2 = rot(prim.x2, prim.y2);
  return { ...prim, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
}

export function translateStitchLineInProject(
  proj: Project,
  pieceId: string,
  stitchId: string,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Project {
  return {
    ...proj,
    pieces: proj.pieces.map((p) =>
      p.id !== pieceId
        ? p
        : {
            ...p,
            stitchLines: pieceStitchLines(p).map((s) =>
              s.id === stitchId
                ? // Re-crop the moved seam to THIS part's outline (#780): moving
                  // a seam so it overlaps the leather at different points than at
                  // creation must drop holes that left the part and restore ones
                  // that came back. Stored seams live in the part's original
                  // space, so crop against the part's own `shapePathData`.
                  translatePivotPoint(
                    translateAndCropStitchLine(
                      s,
                      dx,
                      dy,
                      p.shapePathData,
                      scope,
                    ),
                    dx,
                    dy,
                  )
                : s,
            ),
          },
    ),
  };
}

/** The geometry a resize of the transform target `key` moves, as a snap
 *  exclusion (#1174). A part takes everything on it along; a cutout or seam
 *  moves alone, so the rest of its part stays a valid target. Keys are
 *  "piece:<id>" / "cutout:<pid>:<cid>" / "stitch:<pid>:<sid>". */
function snapExclusionForKey(key: string): DrawSnapExclusion {
  const [kind, pieceId, childId] = key.split(":");
  if (kind === "piece") return { pieceId };
  if (kind === "cutout") return { cutoutId: childId };
  if (kind === "stitch") return { stitchId: childId };
  return {};
}
